import React from 'react';
import { Link } from 'react-router-dom';
import NavigationBar from '@/components/NavigationBar';
import { TERMS_VERSION } from './policyVersions';

const sectionClass = 'mb-7';
const headingClass = 'mb-3 text-2xl font-semibold text-gray-900 dark:text-gray-100';
const textClass = 'mb-3 text-gray-700 dark:text-gray-300';
const listClass = 'mb-3 list-disc space-y-2 pl-6 text-gray-700 dark:text-gray-300';

const Terms = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
    <NavigationBar />
    <main
      data-policy-document="terms"
      data-policy-version={TERMS_VERSION}
      className="mx-auto max-w-4xl p-6 pt-16"
    >
      <h1 className="mb-3 text-center text-4xl font-bold text-gray-900 dark:text-gray-100">
        Pinngro Human Beta Terms of Service
      </h1>
      <p className="mb-8 text-center text-gray-700 dark:text-gray-300">
        Last updated: September 2, 2026 · Human Beta version beta-terms-2026-09-02-v2
      </p>

      <div className="rounded-lg bg-white p-8 shadow-lg dark:bg-gray-800">
        <section className={sectionClass}>
          <h2 className={headingClass}>1. Service operator and agreement</h2>
          <p className={textClass}>
            These Terms are an agreement between you and Pinngro Inc. (“Pinngro,” “we,”
            “us,” or “our”). Pinngro Inc. is a Delaware corporation formed on August 3,
            2026, Delaware State File Number 10720305. Its registered office is 16192
            Coastal Highway, Lewes, Delaware 19958, Sussex County, United States. Its
            registered agent is Harvard Business Services, Inc. Its principal business
            and correspondence address is 144 Triq Il-Bazilika, Mosta MST 1017, Malta.
          </p>
          <p className={textClass}>
            By accepting these Terms and using the invitation-only Human Beta, you agree
            to this contract. If you do not agree, do not register or use Pinngro.
          </p>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>2. Human Beta access</h2>
          <ul className={listClass}>
            <li>Access is limited to people invited by Pinngro and remains subject to email verification, account security checks, suspension, and availability.</li>
            <li>You must be at least 18, have legal capacity, and use the service only where permitted by applicable law.</li>
            <li>An invitation is personal, email-bound, expiring, and may not be transferred or used to bypass admission controls.</li>
            <li>The Human Beta may change, be interrupted, or be withdrawn. Features described as future or unavailable are not part of this release.</li>
          </ul>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>3. What Pinngro currently provides</h2>
          <p className={textClass}>
            Pinngro provides stock-screening, strategy-configuration, historical
            Backtesting, and virtual Practice tools. Stock Focus determines what stocks
            to consider. A Trading Strategy determines how completed evidence becomes a
            suggestion. These tools provide software-generated analytical output, not
            personalized investment advice.
          </p>
          <ul className={listClass}>
            <li>Practice uses virtual funds only. Practice gains, losses, positions, and fills are simulations and are not real assets or transactions.</li>
            <li>Every governed Practice BUY or SELL suggestion requires your explicit approval or rejection in Today. A strategy signal never automatically becomes an order.</li>
            <li>No response, rejection, expiry, failed authorization, or unsupported timing means no Practice transaction.</li>
            <li>An approval authorizes only the bounded virtual Practice attempt described on screen; it does not guarantee a fill or outcome.</li>
            <li>No Alpaca Paper or Live broker-order submission is enabled in this Human Beta. Live trading is not currently available.</li>
          </ul>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>4. Broker connectivity</h2>
          <p className={textClass}>
            If you connect a supported broker such as Alpaca, authorization is tenant-owned
            and uses the broker’s OAuth flow. Pinngro does not ask for your broker password
            or silently fall back to direct API keys. Current connectivity may support
            account or market-data reads, but it does not authorize automatic execution
            and does not enable Pinngro to submit Paper or Live orders. You may disconnect
            Pinngro and should also revoke access through the broker when appropriate.
          </p>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>5. Stripe Test Mode and Premium</h2>
          <ul className={listClass}>
            <li>The Human Beta uses Stripe Test Mode only. Test-card transactions do not create real charges.</li>
            <li>Never enter real card details. Use only Stripe’s published test-card information when instructed during controlled Beta acceptance.</li>
            <li>There is no free trial during the Human Beta.</li>
            <li>Premium unlocks Premium Backtesting only. Factory strategies, Stock Focus, Practice, and Advanced/custom strategy creation remain available independently of Premium, subject to ordinary account eligibility.</li>
            <li>A Checkout redirect is not proof of entitlement. Premium is granted only after Pinngro accepts the corresponding signed Stripe Test Mode subscription event.</li>
            <li>Cancellation at period end retains Premium only until the authoritative test subscription period ends. Failed, canceled, expired, unpaid, or ambiguous subscription state fails closed.</li>
          </ul>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>6. No investment advice and market risk</h2>
          <ul className={listClass}>
            <li>Pinngro is a software provider, not a broker, custodian, investment adviser, or discretionary portfolio manager.</li>
            <li>Screening results, strategy signals, explanations, Backtests, and Practice results are informational software outputs and are not recommendations to buy or sell a security.</li>
            <li>Market data may be delayed, unavailable, incomplete, or incorrect. Past or simulated performance does not predict future results.</li>
            <li>Trading securities involves risk of loss. You remain responsible for independent decisions, taxes, legal compliance, and any activity you undertake outside Pinngro.</li>
          </ul>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>7. Account and security responsibilities</h2>
          <ul className={listClass}>
            <li>Provide accurate registration information and keep your credentials and two-factor authentication secure.</li>
            <li>Do not share invitation or account tokens, attempt unauthorized access, disrupt the service, or use Pinngro unlawfully.</li>
            <li>Tell us promptly at <a className="text-blue-600 hover:underline" href="mailto:support@pinngro.com">support@pinngro.com</a> if you suspect unauthorized account activity.</li>
            <li>We may suspend access, revoke sessions, or restrict functionality to protect users, investigate abuse, comply with law, or enforce these Terms.</li>
          </ul>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>8. Your strategies and Pinngro software</h2>
          <p className={textClass}>
            You retain your rights in strategy configurations and other content you create.
            You grant Pinngro permission to host, process, reproduce, and display that
            content only as needed to operate, secure, support, and improve the service.
            Pinngro and its licensors retain rights in the platform, software, factory
            strategies, designs, and documentation. You may not misuse, resell, reverse
            engineer, or interfere with the service except where applicable law permits.
          </p>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>9. Third-party services</h2>
          <p className={textClass}>
            Pinngro relies on third parties for hosting, email delivery, test billing,
            broker OAuth connectivity, and market or news data. Third-party services have
            their own terms and may be unavailable. Mailgun delivers transactional email
            through authenticated STARTTLS SMTP. Stripe hosts Test Mode Checkout and the
            Test Mode customer portal. Pinngro does not receive or store card details.
          </p>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>10. Account closure and retained evidence</h2>
          <p className={textClass}>
            Self-service account deletion is disabled during the Human Beta. Contact
            <a className="ml-1 text-blue-600 hover:underline" href="mailto:support@pinngro.com">support@pinngro.com</a>
            to request closure. Closure is support-controlled while Pinngro completes the
            approved workflow for active test subscriptions, legal retention, security,
            and immutable billing and Practice evidence. A request does not authorize
            immediate destruction of records that must be retained lawfully or to protect
            the integrity of historical evidence.
          </p>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>11. Availability, warranties, and liability</h2>
          <p className={textClass}>
            The Human Beta is provided on an “as available” basis to the extent permitted
            by law. Pinngro does not promise uninterrupted availability, error-free data,
            a particular result, or future availability of a feature. Nothing in these
            Terms excludes liability or statutory consumer rights that cannot legally be
            excluded. Subject to those mandatory rights, Pinngro is not responsible for
            losses caused by decisions made from analytical output, third-party outages,
            market-data errors, or unauthorized use of your credentials.
          </p>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>12. Privacy</h2>
          <p className={textClass}>
            Our <Link className="text-blue-600 hover:underline" to="/privacy">Privacy Policy</Link>
            explains what information Pinngro processes, why it is processed, the service
            providers involved, retention, security, and your data-protection choices.
          </p>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>13. Governing law and disputes</h2>
          <p className={textClass}>
            These Terms are governed by the laws of Malta, and the courts of Malta have
            jurisdiction, subject always to mandatory consumer protections and rights to
            bring proceedings elsewhere that cannot legally be excluded. Contact
            <a className="ml-1 text-blue-600 hover:underline" href="mailto:support@pinngro.com">support@pinngro.com</a>
            first if you wish to resolve a concern informally.
          </p>
        </section>

        <section>
          <h2 className={headingClass}>14. Changes and contact</h2>
          <p className={textClass}>
            We may update these Terms when the Beta, law, or our operating practices
            change. Material changes require a new policy version and renewed acceptance
            where appropriate. Questions about these Terms may be sent to
            <a className="ml-1 text-blue-600 hover:underline" href="mailto:support@pinngro.com">support@pinngro.com</a>.
            Privacy requests may be sent to
            <a className="ml-1 text-blue-600 hover:underline" href="mailto:privacy@pinngro.com">privacy@pinngro.com</a>.
          </p>
        </section>
      </div>
    </main>
  </div>
);

export default Terms;
